import { CreateClientResponse } from './collections/client/createClient/createClient_200'
import { CreateClientRequest } from './collections/client/createClient/createClient_post'
import { DownloadDataFromAresResponse } from './collections/client/downloadARESData/downloadARESData_200'
import { GetClientResponse } from './collections/client/getClientDetail/getClientDetail_200'
import { GetClientsResponse } from './collections/client/getListOfClients/getListOfClients_200'
import { UpdateClientResponse } from './collections/client/updateClient/updateClient_200'
import { UpdateClientRequest } from './collections/client/updateClient/updateClient_post'
import { handleNetworkResponseAndGetJsonAsync, Network } from './Network'
import { ClientDto, ClientLookupDto } from './sharedTypes/Client'
import { GlobalVars } from '../../global'
import { ClientUniquenessResponse } from './collections/client/validateClientUniqueness/validateClientUniqueness'

export class ClientManager {
    static _instance: ClientManager | null = null

    static getManager(): ClientManager {
        if (this._instance === null) {
            this._instance = new ClientManager()
        }
        return this._instance
    }

    async createClientAsync(client: ClientDto): Promise<CreateClientResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/client/create`
        const body: CreateClientRequest = {
            name: client.name,
            clientType: client.clientType,
            address: client.address,
            companyRegistrationNumber: client.companyRegistrationNumber,
            valueAddedTaxIdentificationNumber: client.valueAddedTaxIdentificationNumber,
            contacts: client.contacts,
            note: client.note,
            notifications: client.notifications,
            lastModifiedAt: new Date(),
            emailMessageSwitches: client.emailMessageSwitches,
        }
        try {
            response = await Network.fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: CreateClientResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async updateClientAsync(id: string, client: UpdateClientRequest): Promise<UpdateClientResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/client/update/${id}`
        const body: UpdateClientRequest = client
        try {
            response = await Network.fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: UpdateClientResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async deleteClientAsync(id: string): Promise<void> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/client/${id}`
        try {
            response = await Network.fetch(url, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: void = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getClientAsync(id: string): Promise<GetClientResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/client/${id}`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: GetClientResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getClientAresAsync(companyRegistrationNumber: number): Promise<DownloadDataFromAresResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/client/ares?CompanyRegistrationNumber=${companyRegistrationNumber}`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: DownloadDataFromAresResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getAllClientsAsync(): Promise<GetClientsResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/client`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: GetClientsResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getLookupClientsAsync(): Promise<{ items: ClientLookupDto[] }> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/client/lookup`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: { items: ClientLookupDto[] } = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getAllFilteredClientsAsync(dateFrom: string, dateTo: string): Promise<GetClientsResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/client?EventsFrom=${dateFrom}&EventsTo=${dateTo}`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: GetClientsResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async validateClientUniquenessAsync(
        client: Pick<ClientDto, 'contacts' | 'name' | 'companyRegistrationNumber'>
    ): Promise<ClientUniquenessResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/client/validate-uniqueness`
        const body = client
        try {
            response = await Network.fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: ClientUniquenessResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }
}
